import dayjs from 'dayjs';

import { DB_DATE_FORMAT } from 'core/constants';
import { ExportEntity, FileType } from 'core/enums';
import { QuotePartExportType } from 'quotes/components/QuotePartList';
import { QuoteType } from 'quotes/enums';

import { IAttributeForm, ISkuExportPreviewResult, ISkuExportRequest } from './types';

// Preview and Download must export the same options, so both build the request here.
// `selectableSpaEnabled` and `spaStatus` are sent only when the user may change them: an absent key
// leaves the stored value untouched, while a stale echo without the grant would refuse the export.
export const mapAttributeFormToSkuExportRequest = ({
  quoteId,
  formData,
  persist,
  canChooseSpaStatus = false,
  canEnableSelectableSpa = false,
  localDate,
}: {
  quoteId: number;
  formData: IAttributeForm;
  persist: boolean;
  canChooseSpaStatus?: boolean;
  canEnableSelectableSpa?: boolean;
  localDate: string;
}): ISkuExportRequest => ({
  quoteId,
  exportType: ExportEntity.QUOTE_PARTS,
  persist,
  localDate,
  options: {
    fileType: FileType.CSV,
    quoteType: QuoteType.VENDOR,
    partExportType: QuotePartExportType.EXTERNAL,
    projectId: formData.allCustomers ? undefined : formData.project?.id,
    customerIds: formData.allCustomers
      ? []
      : formData.customers?.map((customer) => customer.id) ?? [],
    allCustomers: formData.allCustomers,
    allSalesLocations: formData.allSalesLocations,
    salesLocationIds: formData.allSalesLocations
      ? []
      : formData.salesLocations?.map((location) => location.id) ?? [],
    shippingLocationIds: formData.shippingLocations?.map((location) => location.id) ?? [],
    spaPriorityTier: formData.spaPriorityTier,
    // Sent so a drawer change reaches the Preview, which is built from the stored value.
    ...(canChooseSpaStatus && { spaStatus: formData.spaStatus }),
    // Persist-on-export is a full overwrite, so send even when empty to clear the column —
    // and an id (never the email): the BE injects the email itself.
    tradeAgreementOwnerId: formData.tradeAgreementOwner?.id ?? '',
    // Same overwrite rule: omitting this would wipe a date set on the Attributes tab.
    tradeAgreementStartDate: formData.tradeAgreementStartDate
      ? dayjs(formData.tradeAgreementStartDate).format(DB_DATE_FORMAT)
      : '',
    ...(canEnableSelectableSpa && { selectableSpaEnabled: !!formData.selectableSpaEnabled }),
  },
});

export const isSkuExportPreviewResult = (result: unknown): result is ISkuExportPreviewResult =>
  typeof (result as { content?: unknown } | null)?.content === 'string';
